import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Sun, 
  Sunset, 
  Sunrise,
  ChevronLeft,
  ChevronRight,
  Check,
  AlertCircle,
  Zap
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ScheduleSlot {
  id: string;
  date: Date;
  time: string;
  period: 'morning' | 'afternoon' | 'evening';
  label: string;
  description: string;
  icon: React.ReactNode;
  recommended?: boolean;
  conflict?: boolean;
}

interface CalendarSchedulerProps {
  value?: string;
  onChange: (value: string) => void;
  existingSchedules?: string[];
  className?: string;
}

export const CalendarScheduler: React.FC<CalendarSchedulerProps> = ({
  value,
  onChange,
  existingSchedules = [],
  className
}) => {
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [customDate, setCustomDate] = useState('');
  const [customTime, setCustomTime] = useState('');
  const [selectedTimePreset, setSelectedTimePreset] = useState('');

  // Generate smart time suggestions
  const generateSmartSlots = (): ScheduleSlot[] => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);
    
    const dayAfterTomorrow = new Date(now);
    dayAfterTomorrow.setDate(now.getDate() + 2);

    const slots: ScheduleSlot[] = [];

    // TESTING: Allow immediate scheduling - removed 2-hour restriction
    const fiveMinutesFromNow = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutes from now
    const currentHour = fiveMinutesFromNow.getHours();
    
    // Allow same-day scheduling at any time for testing
    // Same day afternoon if current time allows
    if (currentHour < 14) {
      slots.push({
        id: 'today-afternoon',
        date: new Date(now),
        time: Math.max(14, currentHour + 1).toString().padStart(2, '0') + ':00',
        period: 'afternoon',
        label: 'Today Afternoon',
        description: 'Later today - High activity period',
        icon: <Sun className="h-4 w-4" />,
        recommended: true
      });
    }
    
    // Same day evening if current time allows
    if (currentHour < 17) {
      slots.push({
        id: 'today-evening',
        date: new Date(now),
        time: Math.max(17, currentHour + 1).toString().padStart(2, '0') + ':00',
        period: 'evening',
        label: 'Today Evening',
        description: 'Tonight - Prime engagement time',
        icon: <Sunset className="h-4 w-4" />
      });
    }

    // Tomorrow morning
    slots.push({
      id: 'tomorrow-morning',
      date: tomorrow,
      time: '09:00',
      period: 'morning',
      label: 'Tomorrow Morning',
      description: '9:00 AM - Peak engagement time',
      icon: <Sunrise className="h-4 w-4" />,
      recommended: !slots.length // Make tomorrow morning recommended if no same-day options
    });

    // Tomorrow afternoon
    slots.push({
      id: 'tomorrow-afternoon',
      date: tomorrow,
      time: '14:00',
      period: 'afternoon',
      label: 'Tomorrow Afternoon',
      description: '2:00 PM - High activity period',
      icon: <Sun className="h-4 w-4" />
    });

    // Day after tomorrow morning
    slots.push({
      id: 'day-after-morning',
      date: dayAfterTomorrow,
      time: '10:00',
      period: 'morning',
      label: `${dayAfterTomorrow.toLocaleDateString('en', { weekday: 'long' })} Morning`,
      description: '10:00 AM - Optimal reach',
      icon: <Sunrise className="h-4 w-4" />
    });

    // Check for conflicts with existing schedules
    return slots.map(slot => {
      const slotDateTime = new Date(slot.date);
      slotDateTime.setHours(parseInt(slot.time.split(':')[0]), parseInt(slot.time.split(':')[1]));
      
      const hasConflict = existingSchedules.some(existing => {
        const existingDate = new Date(existing);
        const timeDiff = Math.abs(slotDateTime.getTime() - existingDate.getTime());
        return timeDiff < 60 * 60 * 1000; // Within 1 hour
      });

      return { ...slot, conflict: hasConflict };
    });
  };

  const [smartSlots] = useState<ScheduleSlot[]>(generateSmartSlots());

  // Time presets for quick selection
  const timePresets = [
    { value: '09:00', label: '9:00 AM', description: 'Morning peak', icon: <Sunrise className="h-4 w-4" /> },
    { value: '14:00', label: '2:00 PM', description: 'Afternoon rush', icon: <Sun className="h-4 w-4" /> },
    { value: '17:00', label: '5:00 PM', description: 'Evening peak', icon: <Sunset className="h-4 w-4" /> },
    { value: '20:00', label: '8:00 PM', description: 'Prime time', icon: <Sunset className="h-4 w-4" /> }
  ];

  // Auto-apply custom selection when both date and time are selected
  useEffect(() => {
    // Remove auto-application - let users confirm their selection manually
    // This was causing premature scheduling messages and form closing
    // if (customDate && customTime) {
    //   handleCustomSelection();
    // }
  }, [customDate, customTime]);

  // Handle slot selection
  const handleSlotSelect = (slot: ScheduleSlot) => {
    if (slot.conflict) return;
    
    const selectedDateTime = new Date(slot.date);
    selectedDateTime.setHours(parseInt(slot.time.split(':')[0]), parseInt(slot.time.split(':')[1]));
    
    // Convert to UTC ISO string for storage
    const isoString = selectedDateTime.toISOString();
    setSelectedSlot(slot.id);
    onChange(isoString);
  };

  // Custom date picker for advanced users
  const handleCustomDate = (date: Date, time: string) => {
    const selectedDateTime = new Date(date);
    const [hours, minutes] = time.split(':');
    selectedDateTime.setHours(parseInt(hours), parseInt(minutes));
    
    // Convert to UTC ISO string for storage
    const isoString = selectedDateTime.toISOString();
    onChange(isoString);
    setShowCalendar(false);
  };

  // Handle custom datetime selection
  const handleCustomSelection = () => {
    if (!customDate || !customTime) return;
    
    const selectedDateTime = new Date(customDate);
    const [hours, minutes] = customTime.split(':');
    selectedDateTime.setHours(parseInt(hours), parseInt(minutes));
    
    // TESTING: Removed 1-hour minimum validation for immediate testing
    // Just ensure it's in the future (at least 1 minute)
    const now = new Date();
    const oneMinuteFromNow = new Date(now.getTime() + 60 * 1000); // Add 1 minute
    
    if (selectedDateTime <= oneMinuteFromNow) {
      // Show a brief warning but still allow it for testing
      console.warn('Selected time is very soon - this is allowed for testing purposes');
    }
    
    // Convert to UTC ISO string for storage
    const isoString = selectedDateTime.toISOString();
    setSelectedSlot(null); // Clear smart slot selection
    onChange(isoString);
    // Remove auto-close behavior - let user stay in form to make adjustments
    // setShowCalendar(false); // Auto-close the calendar after selection
  };

  // Handle time preset selection
  const handleTimePresetSelect = (timeValue: string) => {
    setSelectedTimePreset(timeValue);
    setCustomTime(timeValue);
    
    // Apply selection only if both date and time are available
    if (customDate) {
      const selectedDateTime = new Date(customDate);
      const [hours, minutes] = timeValue.split(':');
      selectedDateTime.setHours(parseInt(hours), parseInt(minutes));
      
      // Convert to UTC ISO string for storage
      const isoString = selectedDateTime.toISOString();
      setSelectedSlot(null); // Clear smart slot selection
      onChange(isoString);
      // Don't auto-close - let user stay to make further adjustments
    }
  };

  // Get today's date in YYYY-MM-DD format for the min date (allow same-day scheduling)
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Get current time in HH:MM format for minimum time validation
  const getCurrentTime = () => {
    const now = new Date();
    return now.toTimeString().slice(0, 5); // HH:MM format
  };

  // Check if selected date is today
  const isToday = (dateString: string) => {
    const today = new Date().toISOString().split('T')[0];
    return dateString === today;
  };

  const getPeriodColor = (period: string) => {
    switch (period) {
      case 'morning': return 'from-amber-400 to-orange-500';
      case 'afternoon': return 'from-blue-400 to-sky-500';
      case 'evening': return 'from-sky-400 to-blue-500';
      default: return 'from-zinc-400 to-zinc-500';
    }
  };

  const formatSelectedTime = () => {
    if (!value) return null;
    const date = new Date(value);
    return {
      date: date.toLocaleDateString('en', { 
        weekday: 'long', 
        month: 'long', 
        day: 'numeric' 
      }),
      time: date.toLocaleTimeString('en', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })
    };
  };

  const selectedTime = formatSelectedTime();

  return (
    <div className={cn("space-y-4", className)}>
      {/* Remove the premature "Selected Time Display" - this was confusing users */}
      {/* {selectedTime && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-lg p-4"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100">
              <Check className="h-4 w-4 text-green-600" />
            </div>
            <div>
              <p className="font-medium text-green-900">
                Scheduled for {selectedTime.date}
              </p>
              <p className="text-sm text-green-700">
                at {selectedTime.time}
              </p>
            </div>
          </div>
        </motion.div>
      )} */}

      {/* Quick Time Slots */}
      <div className="space-y-3">
        <div className="grid gap-3">
          {smartSlots.map((slot, index) => (
            <motion.div
              key={slot.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card 
                className={cn(
                  "transition-all duration-200 cursor-pointer border-2 bg-card dark:bg-zinc-800",
                  selectedSlot === slot.id 
                    ? "border-primary bg-blue-50 dark:bg-blue-950/30 shadow-md" 
                    : slot.conflict
                    ? "border-red-200 dark:border-red-800/40 bg-red-50 dark:bg-red-950/30 opacity-60 cursor-not-allowed"
                    : "border-border dark:border-zinc-700 hover:border-primary/50 dark:hover:border-primary hover:shadow-sm",
                  slot.recommended && "ring-2 ring-primary/20"
                )}
                onClick={() => handleSlotSelect(slot)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-r text-white shadow-sm",
                        getPeriodColor(slot.period)
                      )}>
                        {slot.icon}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="text-base font-semibold text-foreground">
                            {slot.label}
                          </h3>
                          {slot.recommended && (
                            <Badge className="bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs border border-blue-200 dark:border-blue-800/40">
                              Recommended
                            </Badge>
                          )}
                          {slot.conflict && (
                            <Badge variant="destructive" className="text-xs bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800/40">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Conflict
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed">{slot.description}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <div className="text-lg font-bold text-foreground">
                        {new Date(`2000-01-01T${slot.time}`).toLocaleTimeString('en', { 
                          hour: 'numeric', 
                          minute: '2-digit',
                          hour12: true 
                        })}
                      </div>
                      <div className="text-xs text-muted-foreground font-medium">
                        {slot.date.toLocaleDateString('en', { month: 'short', day: 'numeric' })}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Custom Date Picker Toggle */}
      <div className="pt-4 border-t border-border dark:border-zinc-700">
        <Button
          variant="outline"
          onClick={() => setShowCalendar(!showCalendar)}
          className="w-full gap-2 text-muted-foreground hover:text-foreground border-border dark:border-zinc-700 hover:bg-accent dark:hover:bg-zinc-800 h-11 font-medium transition-all"
        >
          <CalendarIcon className="h-4 w-4" />
          Choose Custom Date & Time
        </Button>
      </div>

      {/* Custom Calendar (collapsed by default) */}
      <AnimatePresence>
        {showCalendar && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <Card className="border-border dark:border-zinc-700 shadow-sm bg-card dark:bg-zinc-800">
              <CardContent className="p-4">
                <div className="space-y-4">
                  {/* Header */}
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-foreground">Custom Scheduling</h4>
                    <Badge variant="outline" className="text-xs bg-muted text-muted-foreground border-border dark:border-zinc-600">
                      Advanced options
                    </Badge>
                  </div>
                  
                  {/* Date Selection */}
                  <div className="space-y-2">
                    <Label htmlFor="custom-date" className="text-sm font-medium text-muted-foreground">
                      Select Date
                    </Label>
                    <Input
                      id="custom-date"
                      type="date"
                      value={customDate}
                      onChange={(e) => {
                        setCustomDate(e.target.value);
                        // If time is also selected, update the parent component
                        if (customTime) {
                          const selectedDateTime = new Date(e.target.value);
                          const [hours, minutes] = customTime.split(':');
                          selectedDateTime.setHours(parseInt(hours), parseInt(minutes));
                          onChange(selectedDateTime.toISOString());
                          setSelectedSlot(null);
                        }
                      }}
                      min={getTodayDate()}
                      className="bg-card dark:bg-zinc-800 border-border dark:border-zinc-700 focus-visible:ring-ring focus-visible:border-primary h-11 text-foreground"
                    />
                  </div>
                  
                  {/* Time Presets */}
                  <div className="space-y-2">
                    <Label className="text-sm font-medium text-muted-foreground">Popular Times</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {timePresets.map((preset) => (
                        <Button
                          key={preset.value}
                          variant="outline"
                          size="sm"
                          onClick={() => handleTimePresetSelect(preset.value)}
                          className={cn(
                            "justify-start gap-2 h-auto py-2 px-3 transition-all",
                            selectedTimePreset === preset.value
                              ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300"
                              : "border-border dark:border-zinc-700 text-muted-foreground hover:bg-accent dark:hover:bg-zinc-800"
                          )}
                        >
                          {preset.icon}
                          <div className="text-left">
                            <div className="font-medium">{preset.label}</div>
                            <div className="text-xs opacity-70">{preset.description}</div>
                          </div>
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Custom Time Input */}
                  <div className="space-y-2">
                    <Label htmlFor="custom-time" className="text-sm font-medium text-muted-foreground">
                      Or Set Custom Time
                    </Label>
                    <Input
                      id="custom-time"
                      type="time"
                      value={customTime}
                      onChange={(e) => {
                        setCustomTime(e.target.value);
                        setSelectedTimePreset(''); // Clear preset selection
                        // If date is also selected, update the parent component
                        if (customDate) {
                          const selectedDateTime = new Date(customDate);
                          const [hours, minutes] = e.target.value.split(':');
                          selectedDateTime.setHours(parseInt(hours), parseInt(minutes));
                          onChange(selectedDateTime.toISOString());
                          setSelectedSlot(null);
                        }
                      }}
                      min={isToday(customDate) ? getCurrentTime() : undefined}
                      className="bg-card dark:bg-zinc-800 border-border dark:border-zinc-700 focus-visible:ring-ring focus-visible:border-primary h-11 text-foreground"
                    />
                  </div>
                  
                  {/* Show a helpful message instead when date and time are selected */}
                  {customDate && customTime && (
                    <div className="pt-3 border-t border-border dark:border-zinc-700">
                      <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800/40 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
                          <Clock className="h-4 w-4" />
                          <span className="text-sm font-medium">
                            Ready to schedule for {new Date(customDate).toLocaleDateString()} at {customTime}
                          </span>
                        </div>
                        <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                          Click "Schedule Post" button to confirm
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CalendarScheduler; 